import { Menu } from '@affine/component';
import { NotificationCountService } from '@affine/core/modules/notification';
import { track } from '@affine/track';
import { useService } from '@toeverything/infra';
import {
  cloneElement,
  type ReactElement,
  type Ref,
  useCallback,
  useRef,
  useState,
} from 'react';

import { STREAMLAIN_DROPDOWN_MENU_OFFSET } from '../dropdown-panel-chrome/chrome';
import { RadixDropdownPanelChrome } from '../dropdown-panel-chrome/radix-chrome';
import { NotificationList } from './list';
import * as styles from './popover.style.css';

/**
 * The one notification inbox popover: opens the NotificationList to the right
 * of the given trigger with the StreamLain dropdown panel chrome (tail
 * pointing at the trigger, grow-out-of-the-tail animation) and tracks inbox
 * opens. The trigger element receives the popover trigger ref.
 */
export const NotificationListPopover = ({
  children,
}: {
  children: (open: boolean) => ReactElement<{ ref?: Ref<HTMLElement> }>;
}) => {
  const notificationCountService = useService(NotificationCountService);
  const triggerRef = useRef<HTMLElement | null>(null);
  const [open, setOpen] = useState(false);

  const handleOpenChange = useCallback(
    (nextOpen: boolean) => {
      if (nextOpen) {
        track.$.sidebar.notifications.openInbox({
          unreadCount: notificationCountService.count$.value,
        });
      }
      setOpen(nextOpen);
    },
    [notificationCountService]
  );

  return (
    <Menu
      rootOptions={{
        open,
        onOpenChange: handleOpenChange,
      }}
      contentOptions={{
        side: 'right',
        align: 'start',
        sideOffset: STREAMLAIN_DROPDOWN_MENU_OFFSET,
        alignOffset: -8,
        collisionPadding: 8,
        className: styles.popover,
      }}
      items={
        <RadixDropdownPanelChrome triggerRef={triggerRef}>
          <NotificationList />
        </RadixDropdownPanelChrome>
      }
    >
      {cloneElement(children(open), { ref: triggerRef })}
    </Menu>
  );
};
